// src/lib/pose.ts

import { PoseLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';
import type { PoseLandmarkerResult } from '@mediapipe/tasks-vision';
import { POSE_CONFIG } from '../config/constants';
import type { PostureLabel, PostureResult } from '../types/triage';
import { detrend, applyHanningWindow, computeFFT, findPeakInRange } from './rppg';

let poseLandmarker: PoseLandmarker | null = null;

export async function initializePoseLandmarker(): Promise<PoseLandmarker> {
  if (poseLandmarker) {
    return poseLandmarker;
  }

  const vision = await FilesetResolver.forVisionTasks(
    "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
  );

  poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: POSE_CONFIG.MODEL_PATH,
      delegate: "GPU",
    },
    runningMode: "VIDEO",
    numPoses: 1,
    minPoseDetectionConfidence: POSE_CONFIG.MIN_DETECTION_CONFIDENCE,
    minTrackingConfidence: POSE_CONFIG.MIN_TRACKING_CONFIDENCE,
  });

  return poseLandmarker;
}

export function disposePoseLandmarker(): void {
  if (poseLandmarker) {
    poseLandmarker.close();
    poseLandmarker = null;
  }
}

interface Point {
  x: number;
  y: number;
}

function midpoint(p1: Point, p2: Point): Point {
  return {
    x: (p1.x + p2.x) / 2,
    y: (p1.y + p2.y) / 2,
  };
}

export function classifyPosture(result: PoseLandmarkerResult): PostureLabel {
  if (!result.landmarks || result.landmarks.length === 0) {
    return "unknown";
  }

  const landmarks = result.landmarks[0];

  // Key landmarks
  const leftShoulder = landmarks[11];
  const rightShoulder = landmarks[12];
  const leftHip = landmarks[23];
  const rightHip = landmarks[24];

  if (!leftShoulder || !rightShoulder || !leftHip || !rightHip) {
    return "unknown";
  }

  // Calculate midpoints
  const shoulderMid = midpoint(leftShoulder, rightShoulder);
  const hipMid = midpoint(leftHip, rightHip);

  // Check if body is horizontal (collapsed)
  const bodyAngle = Math.atan2(
    shoulderMid.y - hipMid.y,
    shoulderMid.x - hipMid.x
  ) * 180 / Math.PI;

  if (Math.abs(bodyAngle) < POSE_CONFIG.BODY_ANGLE_THRESHOLD) {
    return "collapsed";
  }

  // Calculate normalized shoulder-hip distance
  const distance = Math.abs(shoulderMid.y - hipMid.y);

  if (distance < POSE_CONFIG.SHOULDER_HIP_THRESHOLD) {
    return "sitting";
  } else {
    return "standing";
  }
}

export function computeRespiratoryEffort(shoulderYTimeSeries: number[]): number {
  if (shoulderYTimeSeries.length < 3) {
    return 0;
  }

  // Compute peak-to-peak amplitude
  const max = Math.max(...shoulderYTimeSeries);
  const min = Math.min(...shoulderYTimeSeries);
  const amplitude = max - min;

  // Baseline amplitude
  // NOTE: Set to a very high value (1.0) to effectively disable respiratory effort scoring
  // Typical shoulder movement amplitude is 0.04-0.15, so this will always return 0 (Normal)
  // To enable scoring, reduce A0 to a more realistic value (e.g., 0.05 or 0.08)
  const A0 = 1.0;

  // Map to 0-3 scale
  if (amplitude < 0.5 * A0) return 0; // Normal (< 0.5)
  if (amplitude < 1.5 * A0) return 1; // Mild (< 1.5)
  if (amplitude < 2.5 * A0) return 2; // Moderate (< 2.5)
  return 3; // Severe (>= 2.5)
}

export function aggregatePostureResults(
  postureLabels: PostureLabel[]
): PostureResult {
  if (postureLabels.length === 0) {
    return { label: "unknown", confidence: null };
  }

  // Count occurrences
  const counts: Record<PostureLabel, number> = {
    standing: 0,
    sitting: 0,
    collapsed: 0,
    unknown: 0,
  };

  postureLabels.forEach(label => {
    counts[label]++;
  });

  // Find mode (most common)
  let maxCount = 0;
  let mode: PostureLabel = "unknown";

  (Object.keys(counts) as PostureLabel[]).forEach(label => {
    if (counts[label] > maxCount) {
      maxCount = counts[label];
      mode = label;
    }
  });

  // Confidence = proportion of frames with mode label
  const confidence = maxCount / postureLabels.length;

  return { label: mode, confidence };
}

export function computeRespiratoryRate(
  shoulderYTimeSeries: number[],
  fps: number = 3 // Pose detection runs at 3 Hz
): { rr_bpm: number | null; confidence: number | null } {
  console.log('🫁 Respiratory Rate Debug - Shoulder samples:', shoulderYTimeSeries.length);

  if (shoulderYTimeSeries.length < 10) {
    console.warn('❌ Not enough shoulder samples for RR');
    return { rr_bpm: null, confidence: null };
  }

  try {
    // Check signal quality
    const mean = shoulderYTimeSeries.reduce((a, b) => a + b, 0) / shoulderYTimeSeries.length;
    const variance = shoulderYTimeSeries.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / shoulderYTimeSeries.length;
    const stdDev = Math.sqrt(variance);
    console.log('🫁 Shoulder movement - Mean:', mean.toFixed(4), 'StdDev:', stdDev.toFixed(4));

    // 1. Detrend
    const detrended = detrend(shoulderYTimeSeries);
    console.log('🫁 After detrend - Range:', Math.min(...detrended).toFixed(4), 'to', Math.max(...detrended).toFixed(4));

    // 2. Apply windowing
    const windowed = applyHanningWindow(detrended);

    // 3. Compute FFT
    const spectrum = computeFFT(windowed);
    console.log('🫁 FFT spectrum length:', spectrum.length);

    // 4. Find RR peak in breathing frequency range
    // Normal respiratory rate: 12-20 breaths/min = 0.2-0.33 Hz
    // Wider range: 9-30 breaths/min = 0.15-0.5 Hz
    const RR_FREQ_RANGE: readonly [number, number] = [0.15, 0.5]; // 9-30 bpm
    const rrPeak = findPeakInRange(spectrum, RR_FREQ_RANGE, fps);

    if (!rrPeak) {
      console.warn('❌ No respiratory peak found in range');
      return { rr_bpm: null, confidence: null };
    }

    const rr_bpm = rrPeak.frequency * 60;
    console.log('🫁 RR Peak:', rrPeak.frequency.toFixed(3), 'Hz (', rr_bpm.toFixed(1), 'bpm), power:', rrPeak.power.toFixed(2));

    // Compute confidence based on peak power and signal quality
    const totalPower = spectrum.reduce((sum, val) => sum + val * val, 0);
    const peakPowerRatio = (rrPeak.power * rrPeak.power) / totalPower;
    const confidence = Math.min(1, peakPowerRatio * 5); // Scale up for better discrimination

    console.log('🫁 RR Confidence:', (confidence * 100).toFixed(1) + '%');

    // Only return if confidence is reasonable (>10%)
    if (confidence < 0.1) {
      console.warn('❌ Respiratory rate confidence too low');
      return { rr_bpm: null, confidence };
    }

    return { rr_bpm, confidence };
  } catch (error) {
    console.error('Respiratory rate computation error:', error);
    return { rr_bpm: null, confidence: null };
  }
}
